import { useState, useCallback } from 'react'
import { useFunctionsQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { generateFunctionCall, navigateToQueryEditor } from '@/utils/functionExecution'
// Define interface for database function
interface DatabaseFunction {
  name: string
  schema?: string
  returnType?: string
  language?: string
  definition?: string
  owner?: string
  comment?: string
  arguments?: string  // Add arguments property for function overloading
}
import {
  CodeBracketIcon,
  MagnifyingGlassIcon,
  PlayIcon
} from '@heroicons/react/24/outline'

interface FunctionsViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onFunctionSelect?: (functionName: string, functionArguments?: string) => void
}

export function FunctionsView({ connection, database, schema, onFunctionSelect }: FunctionsViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const { data: functionsResponse, isLoading, error } = useFunctionsQuery(
    connection.id,
    database,
    schema,
    { enabled: true, databaseType: connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL' }
  )

  const allFunctions = Array.isArray(functionsResponse) ? functionsResponse : []

  // Filter and sort functions
  const filteredAndSortedFunctions = allFunctions
    .filter((func: DatabaseFunction) => {
      if (!searchTerm) return true
      return func.name.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: DatabaseFunction, b: DatabaseFunction) => {
      const aValue = a.name || ''
      const bValue = b.name || ''
      const comparison = aValue.localeCompare(bValue)
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  const getSortIcon = () => {
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  const handleExecuteFunction = useCallback((func: DatabaseFunction) => {
    if (!func.name) return
    
    try {
      // Generate the function call query
      const dbType: 'mysql' | 'postgresql' = connection.type === 'mysql' ? 'mysql' : 'postgresql'
      const query = generateFunctionCall(
        func.name,
        func.arguments,
        dbType,
        schema,
        database
      )
      
      // Navigate to query editor with the pre-filled query
      navigateToQueryEditor(connection, query, database, schema, true)
    } catch (error) {
      console.error('Error generating function call:', error)
    }
  }, [connection, database, schema])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading functions: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <CodeBracketIcon className="w-6 h-6 mr-2 text-purple-600" />
              Functions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedFunctions.length} functions
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search functions by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
            />
          </div>
        </div>
      </div>

      {/* Functions Table */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          {/* Table Header */}
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="grid grid-cols-10 gap-4 px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div 
                className="col-span-8 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={handleSort}
              >
                Function Name
                {getSortIcon()}
              </div>
              <div className="col-span-2">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedFunctions.length === 0 ? (
              <div className="text-center py-12">
                <CodeBracketIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No functions match your criteria' : 'No functions found'}
                </p>
              </div>
            ) : (
              filteredAndSortedFunctions.map((func: DatabaseFunction, index: number) => (
                <div key={`${database}-${schema || 'default'}-function-${func.name || 'unnamed'}-${index}`} className="grid grid-cols-10 gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="col-span-8 flex items-center">
                    <CodeBracketIcon className="w-4 h-4 text-purple-600" />
                    <div className="ml-2">
                      <button type="button"
                        onClick={() => func.name && onFunctionSelect?.(func.name, func.arguments)}
                        className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors"
                        title="View function details"
                        disabled={!func.name}
                      >
                        {func.name}
                      </button>
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center space-x-2">
                    <button type="button"
                      onClick={() => func.name && onFunctionSelect?.(func.name, func.arguments)}
                      className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      title="View definition"
                      disabled={!func.name}
                    >
                      <CodeBracketIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                    <button type="button"
                      onClick={() => { handleExecuteFunction(func) }}
                      className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      title="Execute function"
                      disabled={!func.name}
                    >
                      <PlayIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedFunctions.length} of {allFunctions.length} functions
          </div>
        </div>
      </div>
    </div>
  )
}
